import { captureRemixErrorBoundaryError } from "@sentry/remix"
import { cssBundleHref } from "@remix-run/css-bundle"
import type { LinksFunction } from "@remix-run/node"
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "@remix-run/react"

import "./components/styles/reset.css"
import { body, themeClass, vars } from "./components/styles/theme.css"
import "./components/styles/fontimport.css"
import { Toaster } from "react-hot-toast"
import { h1Style } from "./routes/index.css"
import LinkButton from "./components/LinkButton"
import type { ReactNode } from "react"
import * as Sentry from "@sentry/remix"
import Logo from "./components/Logo"
import { errorButtons } from "./routes/generate.css"
import { bodyStyle } from "./routes/rescape.css"
import { ExternalScripts } from "remix-utils/external-scripts"

export const links: LinksFunction = () => [
  ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []),
  // SEO FIX: Added favicon - place your favicon.ico file in the /public folder
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
  // Optional: also add a PNG favicon for modern browsers
  // { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
  // { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
]

function pageTemplate(content: ReactNode, title?: string) {
  return (
    <html lang="en" className={`${themeClass}`}>
      <head>
        <title>{title || "Oops!"}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body
        className={body}
        style={{
          minHeight: "100lvh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            maxWidth: "90%",
            margin: "0 auto",
          }}
        >
          {content}
        </div>
      </body>
    </html>
  )
}
const maintenance = () => {
  return (
    <>
      <Logo />
      <h1
        className={h1Style}
        style={{
          maxWidth: "85%",
        }}
      >
        Essential Security work underway
      </h1>
      <p
        className={bodyStyle}
        style={{
          textAlign: "center",
          width: "75%",
          maxWidth: "1024px",
          fontSize: "18px",
          marginTop: ".8rem",
        }}
      >
        Hi, Rescape AI is temporarily unavailable due to essential security work. We hope
        to be back up and running 8th July 13:00 UTC+1. In the meantime, read about using
        Rescape AI below.
      </p>
      <p
        style={{
          textAlign: "center",
          width: "75%",
          maxWidth: "1024px",
          fontSize: "18px",
          marginTop: ".6rem",
        }}
      >
        If you want to manage your subscription, please email{" "}
        <span style={{ color: vars.color.brand }}>contact@rescapeai.com</span>
      </p>
      <LinkButton url="/article/contemporary-garden-ideas-with-rescape-ai">
        How it works
      </LinkButton>
    </>
  )
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  captureRemixErrorBoundaryError(error)

  const fourOFour = () => {
    return (
      <>
        <Logo />
        <h1 className={h1Style}>404</h1>
        <LinkButton url="/generate">Continue</LinkButton>
      </>
    )
  }

  const somethingBad = () => {
    return (
      <>
        <Logo />
        <h1
          className={h1Style}
          style={{
            textAlign: "center",
            maxWidth: "85%",
          }}
        >
          Something unexpected happened.
        </h1>

        <div className={errorButtons}>
          <LinkButton url="/generate">Try again!</LinkButton>
          <LinkButton
            url=""
            onClick={() => {
              document.cookie = `rescape=; Path=/; SameSite=Lax; Expires=${new Date(
                Date.now()
              ).toUTCString()}`
              window.location.reload()
            }}
            style={{
              background: vars.color.dark2,
              borderColor: "transparent",
              color: vars.color.brand,
            }}
          >
            Log out
          </LinkButton>
        </div>
      </>
    )
  }

  if (isRouteErrorResponse(error)) {
    return pageTemplate(fourOFour())
  }

  Sentry.captureException(error)

  return pageTemplate(somethingBad())
}

export default function App() {
  // const location = useLocation()

  // if (!location.pathname.includes("/article") && location.pathname !== "/") {
  //   return pageTemplate(maintenance(), "Essential Maintenance")
  // }

  return (
    <html lang="en" className={`${themeClass}`}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <ExternalScripts />
        <script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          rel="preload"
        />
        {/* <!-- Fathom - beautiful, simple website analytics --> */}
        <script
          src="https://cdn.usefathom.com/script.js"
          data-spa="auto"
          data-site="EEXUIUHU"
          defer
        ></script>
        {/* <!-- / Fathom --> */}
      </head>
      <body className={body}>
        <Outlet />
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
        <Toaster
          toastOptions={{
            style: {
              wordBreak: "break-word",
              backgroundColor: vars.color.dark2,
              color: vars.color.light3,
            },
          }}
        />
      </body>
    </html>
  )
}
